import { Priority, PRIORITIES, ChecklistState, ChecklistSection } from "./state";

/**
 * Interface to encapsulate stats across a specific section
 */
export interface SectionStats {
    total: number;
    checked: number;
    completionPercentage: number;
    byPriority: Record<Priority, number>;
    checkedByPriority: Record<Priority, number>;
    completionByPriority: Record<Priority, number>;

}

/**
 * Interface to encapsulate stats across the entire checklist
 */
export interface ChecklistStats {
    total: number;
    checked: number;
    completionPercentage: number;

    byPriority: Record<Priority, number>;
    checkedByPriority: Record<Priority, number>;
    completionByPriority: Record<Priority, number>;

    bySection: Record<string, SectionStats>;
}

/**
 * Intialize an empty priority counter
 * @returns
 */
function emptyPriorityCounts(): Record<Priority, number> {
    return {
        critical: 0,
        essential: 0,
        recommended: 0,
        advanced: 0
    };
}

/**
 * Calculates the completion (percent) by priority
 * @param totalByPriority
 * @param checkedByPriority
 * @returns completion (in percent) per Priority
 */
function calculatePriorityCompletion(
  totalByPriority: Record<Priority, number>,
  checkedByPriority: Record<Priority, number>
): Record<Priority, number>  {

  const completionByPriority: Record<Priority, number> = emptyPriorityCounts();

  for ( const prio of PRIORITIES ) {
    completionByPriority[prio] = totalByPriority[prio] > 0 ? Math.round((checkedByPriority[prio] / totalByPriority[prio]) * 100) : 0;
  }

  return completionByPriority

}

/**
 * Calculates statistics of a section
 *  total number of items, total number of items checked, overall completion (percent)
 *  as well as per Priority.
 * @param section
 * @returns
 */
export function getSectionStats(
    section: ChecklistSection
): SectionStats {
    // init the SectionStats
    const stats: SectionStats = {
        total: 0,
        checked: 0,
        completionPercentage: 0,
        byPriority: emptyPriorityCounts(),
        checkedByPriority: emptyPriorityCounts(),
        completionByPriority: emptyPriorityCounts(),
    };

    // section.items ?? {} - guards against no items
    for (const item of Object.values(section.items ?? {} )) {
        stats.total++;

        stats.byPriority[item.priority]++;

        if (item.checked) {
            stats.checked++;

            stats.checkedByPriority[item.priority]++;
        }
    }

    stats.completionPercentage = stats.total > 0 ? Math.round((stats.checked / stats.total) * 100) : 0;
    stats.completionByPriority = calculatePriorityCompletion(stats.byPriority, stats.checkedByPriority)

    return stats;
}

/**
 * Calculates the overall statistics of the checklist
 * number of items, items checked, percentage complete - both overall and per Prioirty.
 * Contains also the individual sections and their statistics.
 * @param state
 * @returns
 */
export function getChecklistStats(
    state: ChecklistState
): ChecklistStats {

    const stats: ChecklistStats = {
        total: 0,
        checked: 0,
        completionPercentage: 0,
        byPriority: emptyPriorityCounts(),
        checkedByPriority: emptyPriorityCounts(),
        completionByPriority: emptyPriorityCounts(),
        bySection: {}
    };

    // create a set to hold only 1 copy of an itemID
    const seenItems = new Set<string>();

    // loop through every section after enumeration
    for (const [sectionName, section] of Object.entries(state)) {
      const sectionStats = getSectionStats(section);

      stats.bySection[sectionName] = sectionStats;

      // loop through all items after enumeration
      // ... section.items ?? {} - guards against no items
      for (const [itemId, item] of Object.entries(section.items ?? {} )) {
          // checck if an item has already been seen
          if (seenItems.has(itemId)) {
            continue;
          }
          seenItems.add(itemId);
          stats.total++;
          stats.byPriority[item.priority]++;
          if (item.checked) {
            stats.checked++;
            stats.checkedByPriority[item.priority]++;
          }

      }

    }

    // Caclulate completion percentage if stats.total is greater than 0,
    // otherwise return 0
    stats.completionPercentage = stats.total > 0 ? Math.round((stats.checked / stats.total) * 100) : 0;

    stats.completionByPriority = calculatePriorityCompletion(stats.byPriority, stats.checkedByPriority)

    return stats;
}
